<template>
  <section id="entity-details" key="entity-details">
    <Breadcrumbs
      :breadcrumbs="breadcrumbs"
    >
      <div slot="breadcrumbAfter" class="d-flex align-items-center" v-if="entity">
        <div class="mr-3">
          <va-popover
            class="ml-1"
            v-if="entity.facebook_id"
            placement="top"
            :message="entity.facebook_id">
            <span class="i-facebook-logo" @click="redirectTo('facebook')"></span>&nbsp;
          </va-popover>
          <va-popover
            class="ml-1"
            v-if="entity.twitter_id"
            placement="top"
            :message="entity.twitter_id">
            <span class="i-twitter-logo" @click="redirectTo('twitter')"></span>&nbsp;
          </va-popover>
          <va-popover
            class="ml-1"
            v-if="entity.linkedin_id"
            placement="top"
            :message="entity.linkedin_id">
            <span class="i-linkedin-logo" @click="redirectTo('linkedin')"></span>
          </va-popover>&nbsp;
        </div>
        <div class="mr-3" v-if="entity && entity.icon_url">
          <img :key="imageTimestamp" class="entity-logo" :src="entity.icon_url" alt="Entity Logo">
        </div>
        <va-popover
          v-if="entity.id == 2 && isFromEvollu && hasWritePermissionsToEntity"
          class="ml-1 mr-2"
          message="Upload Excel Hierarquias Adjustt"
          placement="top">
          <span class="i-file-upload" @click.prevent="isUploadingExcel = !isUploadingExcel"></span>
        </va-popover>
        <va-popover
          v-if="hasWritePermissionsToEntity"
          class="ml-1"
          :message="$t(`entities.status.${entity.status}`)"
          placement="top">
          <span v-if="entity.status === 0" class="i-evollu-status-active"></span>
          <span v-if="entity.status === 1" class="i-evollu-status-pending"></span>
          <span v-if="entity.status === 2" class="i-evollu-status-inactive"></span>
        </va-popover>
        <va-dropdown
          v-if="hasWritePermissionsToEntity"
          class="custom-dropdown"
          fixed>
          <span slot="anchor">
            <slot>
              <div class="pl-3 pr-2 clickable">
                <span class="fa fa-ellipsis-v" style="font-size: 1.5rem;"></span>
              </div>
            </slot>
          </span>
          <div class="contact-dropdown__content pl-3 pr-2 pt-2 pb-2">
            <div
              class="contact-dropdown__item row align--center pl-3 pt-1 pb-1 mt-2 mb-2"
              @click.prevent="updateEntity"
            >
              <i class="fa fa-pencil"></i>&nbsp;
              <span class="pl-2">{{$t('people.actions.edit')}}</span>
            </div>
          </div>
        </va-dropdown>
      </div>
    </Breadcrumbs>

    <section v-if="entity">
      <EntityInformations
        id="entity-informations"
        ref="entity-informations"
        :entity="entity"
        class="mb-5"
      />

      <FacilityList
        id="facilities"
        ref="facilities"
        :entity="entity"
        :collapsed="!(activeSection === 'facilities')"
        class="mb-5"
      />

      <EntityBusinessPlan
        id="business-plan"
        ref="business-plan"
        :entity="entity"
        :collapsed="!(activeSection === 'business-plans')"
        class="mb-5"
      />

      <EntityResponsibles
        id="responsibles"
        ref="responsibles"
        v-if="hasReadResponsiblesPermissions"
        :entity="entity"
        :collapsed="!(activeSection === 'responsibles')"
        class="mb-5"
      />

      <KiosksTable
        id="kiosks"
        ref="kiosks"
        :entity="entity"
        :shouldShowFilters="false"
        :collapsed="!(activeSection === 'kiosks')"
        :collapsable="true"
        :limit="25"
        :shouldShowLimit="false"
        class="mb-5"
      />

      <EntityUsers
        id="users"
        ref="users"
        v-if="hasReadUsersPermissions"
        :collapsed="!(activeSection === 'users')"
        :entity="entity"
        class="mb-5"
      />

      <EntityActivities
        id="activities"
        ref="activities"
        v-if="hasReadUsersPermissions && hasReadActivitiesPermissionsToEntity"
        :collapsed="!(activeSection === 'activities')"
        :entity="entity"
        class="mb-5"
      />

      <EntityHearingLossRules
        id="hearing-loss-rules"
        ref="hearing-loss-rules"
        v-if="isFromEvollu"
        :collapsed="!(activeSection === 'hearing-loss-rules')"
        :entity="entity"
        class="mb-5"
      />

      <EntityActivityProviders
        id="activity-providers"
        ref="activity-providers"
        v-if="isFromEvollu"
        :collapsed="!(activeSection === 'activity-providers')"
        :entity="entity"
        class="mb-5"
      />

      <EntityUsersPermissions
        id="users-permissions"
        ref="users-permissions"
        v-if="isFromEvollu"
        :collapsed="!(activeSection === 'users-permissions')"
        :entity="entity"
        class="mb-5"
      />

      <CreateOrUpdateEntity
        id="entity-form"
        v-if="isUpdatingEntity"
        :entity="entity"
        @entity-updated="onEntityUpdated"
        @cancel="cancelUpdateEntity"
      />
    </section>

    <UploadAdjustt v-if="isUploadingExcel && isFromEvollu"
      @modalWasClosed="onUploadAdjusttModalWasClosed">
    </UploadAdjustt>

  </section>
</template>

<script>
import { EntityService } from '@/api/AuthService'
import KiosksTable from '@/components/kiosks/KiosksTable'
import Breadcrumbs from '@/components/common/Breadcrumbs'
import IsFromEvolluMixin from '@/mixins/IsFromEvolluMixin'
import EntityUsers from '@/components/entities/EntityUsers'
import FacilityList from '@/components/entities/facilities/FacilityList'
import EntityResponsibles from '@/components/entities/EntityResponsibles'
import EntityBusinessPlan from '@/components/entities/EntityBusinessPlan'
import EntityInformations from '@/components/entities/EntityInformations'
import CreateOrUpdateEntity from '@/components/entities/CreateOrUpdateEntity'
import EntityHearingLossRules from '@/components/entities/EntityHearingLossRules'
import EntityActivities from '@/components/entities/EntityActivities'
import EntityActivityProviders from '@/components/entities/EntityActivityProviders'
import EntityUsersPermissions from '@/components/entities/EntityUsersPermissions'
import UploadAdjustt from '@/components/entities/UploadAdjustt'

const usersInfoType = 100
const entitiesInfoType = 110
const activitiesInfoType = 140
const readAccessType = 1
const writeAccessType = 2

export default {
  components: {
    EntityUsers,
    KiosksTable,
    FacilityList,
    Breadcrumbs,
    EntityHearingLossRules,
    EntityActivities,
    EntityBusinessPlan,
    EntityInformations,
    EntityResponsibles,
    CreateOrUpdateEntity,
    EntityActivityProviders,
    EntityUsersPermissions,
    UploadAdjustt
  },
  mixins: [
    IsFromEvolluMixin
  ],
  created () {
    this.checkRouteParams()
    if (!this.entity) {
      this.selectEntity(this.$route.params.id)
    }
  },
  mounted () {
    if (this.activeSection) {
      this.scrollToSection()
      this.sectionFilters = Object.keys(this.queryParams).reduce((filters, key) => {
        if (key === 'section') return filters
        return {
          ...filters,
          [key]: this.queryParams[key]
        }
      }, {})
    }
  },
  computed: {
    activeSection: function () {
      return this.queryParams && Object.keys(this.queryParams).includes('section') ? this.queryParams.section : null
    },
    authService: function () {
      return this.$store.state.services.find(service => service.url === this.$store.state.evollauth_server) || null
    },
    breadcrumbs: function () {
      return [
        { title: this.$t('menu.configurations'), disabled: true },
        { title: this.$t('menu.entities'), meta: { type: 'string', value: `/${this.authService.slug}/entities` } },
        { title: this.entity ? this.entity.name : null, disabled: true }
      ]
    },
    hasWritePermissionsToEntity: function () {
      if (this.entity !== null && this.authService !== null) {
        return this.authService.user_permissions.some(p => p.info_type === entitiesInfoType && p.access_type >= writeAccessType && (p.entity_id === null || p.entity_id === this.entity.id))
      }
      return false
    },
    hasReadUsersPermissions: function () {
      if (this.entity !== null && this.authService !== null) {
        return this.authService.user_permissions.some(p => p.info_type === usersInfoType && p.access_type >= readAccessType && (p.entity_id === null || p.entity_id === this.entity.id))
      }
    },
    hasReadActivitiesPermissionsToEntity: function () {
      if (this.entity !== null && this.authService !== null) {
        return this.authService.user_permissions.some(p => p.info_type === activitiesInfoType && p.access_type >= readAccessType && (p.entity_id === null || p.entity_id === this.entity.id))
      }
    },
    hasReadResponsiblesPermissions: function () {
      return this.$auth.user().entity.id !== 8001
    }
  },
  data: function () {
    return {
      entity: null,
      isUpdatingEntity: false,
      queryParams: null,
      sectionFilters: null,
      isUploadingExcel: false,
    }
  },
  methods: {
    redirectTo: function (socialNetwork) {
      console.log('redirectTo', socialNetwork)
      let redirect
      switch (socialNetwork) {
        case 'linkedin':
          redirect = `https://linkedin.com/company/${this.entity.linkedin_id}`
          break
        case 'facebook':
          redirect = `https://facebook.com/${this.entity.facebook_id}`
          break
        case 'twitter':
          redirect = `https://twitter.com/${this.entity.twitter_id}`
          break
      }
      window.open(redirect, '_blank')
    },
    checkRouteParams: function () {
      if (Object.keys(this.$route.query).length) {
        this.queryParams = this.$route.query
      }
    },
    scrollToSection: function () {
      setTimeout(() => {
        let element = document.querySelector(`#${this.activeSection}`)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' })
        }
      }, 500)
    },
    selectEntity: function (entityId) {
      EntityService.get(entityId)
        .then(entity => this.setEntity(entity))
        .catch(errors => {
          this.$router.go(-1)
        })
    },
    updateEntity: function () {
      this.isUpdatingEntity = true
    },
    cancelUpdateEntity: function () {
      this.isUpdatingEntity = false
    },
    onEntityUpdated: function ({ entity }) {
      this.setEntity(entity)
    },
    setEntity: function (entity) {
      this.entity = entity
      this.imageTimestamp = (new Date()).getMilliseconds()
    },
    setSectionFilters: function () {
      this.$nextTick(() => {
        let reference = this.$refs[this.activeSection]
        if (Object.keys(reference).includes('setFilters') && typeof reference.setFilters === 'function') {
          reference.setFilters(this.sectionFilters)
        }
      })
    },
    onUploadAdjusttModalWasClosed: function () {
      this.isUploadingExcel = false
    },
  },
  watch: {
    entity: function (entity) {
      if (this.sectionFilters && Object.keys(this.sectionFilters).length) {
        this.setSectionFilters()
      }
    },
    sectionFilters: function (filters) {
      if (this.entity) {
        this.setSectionFilters()
      }
    }
  },
  beforeRouteEnter (to, from, next) {
    if (to.params.id !== undefined) {
      next(vm => vm.selectEntity(parseInt(to.params.id)))
    } else {
      next()
    }
  },
  beforeRouteUpdate (to, from, next) {
    if (to.params.id !== undefined) {
      this.selectEntity(parseInt(to.params.id))
    }
    next()
  }
}
</script>
